import {cn} from '@tempmee/core'
import {type CSSProperties, type HTMLAttributes, forwardRef} from 'react'
import {buildBoxClasses, buildFlexItemClasses} from './Variants'
import type {FlexItemProps, SpacingScale} from './Variants'

export interface BoxProps extends HTMLAttributes<HTMLDivElement>, FlexItemProps {
  /** Render as a different HTML element. @default 'div' */
  as?: 'div' | 'span'
  /** Padding on all sides. Uses the spacing scale (0–24). */
  p?: SpacingScale
  /** Horizontal padding (left + right). Uses the spacing scale (0–24). */
  px?: SpacingScale
  /** Vertical padding (top + bottom). Uses the spacing scale (0–24). */
  py?: SpacingScale
  /** Padding top. Uses the spacing scale (0–24). */
  pt?: SpacingScale
  /** Padding right. Uses the spacing scale (0–24). */
  pr?: SpacingScale
  /** Padding bottom. Uses the spacing scale (0–24). */
  pb?: SpacingScale
  /** Padding left. Uses the spacing scale (0–24). */
  pl?: SpacingScale
  /** Sets both width and height to the same value. Overridden by explicit `width` or `height` props. */
  size?: SpacingScale | string
  /** Width. Pass a SpacingScale number for a Tailwind class, or a string like '100%' or 'auto' for an inline style. */
  width?: SpacingScale | string
  /** Height. Pass a SpacingScale number for a Tailwind class, or a string like '100%' or 'auto' for an inline style. */
  height?: SpacingScale | string
}

export const Box = forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      as: Component = 'div',
      className,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      size,
      width,
      height,
      flex,
      grow,
      shrink,
      minWidth,
      alignSelf,
      style,
      ...props
    },
    ref,
  ) => {
    const spacingClasses = buildBoxClasses({p, px, py, pt, pr, pb, pl, size, width, height})
    const flexItemClasses = buildFlexItemClasses({flex, grow, shrink, minWidth, alignSelf})

    const inlineStyle: CSSProperties = {}
    if (typeof size === 'string') {
      inlineStyle.width = size
      inlineStyle.height = size
    }
    if (typeof width === 'string') inlineStyle.width = width
    if (typeof height === 'string') inlineStyle.height = height

    return (
      <Component
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(spacingClasses, flexItemClasses, className)}
        style={{...inlineStyle, ...style}}
        {...props}
      />
    )
  },
)

Box.displayName = 'Box'
